import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import EducatorLayout from './components/EducatorLayout';
import api from '../api/api';

const TEXT_COLOR = 'rgb(0, 21, 74)';
const CARD_RADIUS = 3;

const EducatorAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/notifications');
        if (!isMounted) return;
        setItems(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const announcements = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [items]
  );

  const formatDateTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  };

  return (
    <EducatorLayout>
      <Box sx={{ color: TEXT_COLOR }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: TEXT_COLOR }}>
              Announcements
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
              Latest updates for educators across all programmes.
            </Typography>
          </Box>

          <Card elevation={4} sx={{ borderRadius: CARD_RADIUS }}>
            <CardContent>
              {loading ? (
                <Stack alignItems="center" spacing={2} py={4}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" sx={{ color: TEXT_COLOR }}>
                    Loading announcements...
                  </Typography>
                </Stack>
              ) : announcements.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  No announcements have been shared yet.
                </Alert>
              ) : (
                <List disablePadding>
                  {announcements.map((announcement, index) => (
                    <React.Fragment key={announcement._id || announcement.createdAt || index}>
                      <ListItem alignItems="flex-start" disableGutters>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight={700} sx={{ color: TEXT_COLOR }}>
                              {announcement.title || 'Untitled announcement'}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={1}>
                              <Typography
                                variant="caption"
                                sx={{ color: TEXT_COLOR, fontWeight: 600, letterSpacing: 0.2 }}
                              >
                                {formatDateTime(announcement.createdAt)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: TEXT_COLOR, whiteSpace: 'pre-wrap' }}>
                                {announcement.body || announcement.message || 'No details provided.'}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index !== announcements.length - 1 && <Divider component="li" sx={{ my: 2 }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorAnnouncements;
